import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type {
    ExportPerformanceResultsQuery,
    ExportPerformanceResultsQueryVariables,
} from '../bulk/BulkModification.api';
import { ExportPerformanceResultsDocument } from '../bulk/BulkModification.api';
import client from '@/apolloClient';
import type { PerformanceResultType, PerformanceResultFilterRule } from '@/apolloClient.types';
import { error as showErrorNotification } from '@/utils/notifications/actions';

/** Filter options for exporting performance results */
export type ExportPerformanceResultsFilters = PerformanceResultFilterRule | undefined;

const useExportData = () => {
    const { t } = useTranslation('performanceResult');

    const exportData = useCallback(
        async (resultType: PerformanceResultType, filters?: ExportPerformanceResultsFilters) => {
            try {
                const variables: ExportPerformanceResultsQueryVariables = {
                    resultType,
                    filter: filters,
                };

                const { data } = await client.query<ExportPerformanceResultsQuery>({
                    query: ExportPerformanceResultsDocument,
                    variables,
                    fetchPolicy: 'network-only',
                });

                if (data?.exportPerformanceResults?.__typename === 'ErrorResponse') {
                    const errorResponse = data.exportPerformanceResults;
                    const specificErrorField = errorResponse.fields?.[0];
                    const errorMessage = specificErrorField?.message || t('bulk.exportDataErrorDesc');
                    showErrorNotification({
                        message: t('bulk.exportDataErrorTitle'),
                        description: errorMessage,
                    });
                } else {
                    const downloadUrl = data?.exportPerformanceResults.result.downloadUrl;
                    const link = document.createElement('a');
                    link.href = downloadUrl as string;
                    link.download = 'performance_results.xlsx';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            } catch (error) {
                console.error('Error exporting data:', error);
                throw error;
            }
        },
        [t]
    );

    return { exportData };
};

export default useExportData;
